/**
 * Authentication utilities for secure token handling
 */

// Constants for storage keys
const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRY_KEY = 'token_expiry';
const USER_INFO_KEY = 'user_info';

/**
 * Securely store authentication token with expiration
 * @param {string} token - JWT token
 * @param {string} refreshToken - Refresh token
 * @param {number} expiresIn - Expiration time in seconds
 */
export const storeAuthToken = (token, refreshToken, expiresIn) => {
  if (!token) return;
  
  // Calculate expiry time
  const expiryTime = Date.now() + expiresIn * 1000;
  
  // Use sessionStorage for better security (tokens are cleared when browser is closed)
  sessionStorage.setItem(TOKEN_KEY, token);
  
  if (refreshToken) {
    sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
  
  sessionStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
};

/**
 * Store user information
 * @param {Object} userInfo - User information object
 */
export const storeUserInfo = (userInfo) => {
  if (!userInfo) return;
  
  // Store user info without sensitive data
  const safeUserInfo = { ...userInfo };
  delete safeUserInfo.password;
  delete safeUserInfo.token;
  
  sessionStorage.setItem(USER_INFO_KEY, JSON.stringify(safeUserInfo));
};

/**
 * Get the stored authentication token
 * @returns {string|null} The stored token or null if not found/expired
 */
export const getAuthToken = () => {
  const token = sessionStorage.getItem(TOKEN_KEY);
  const expiryTime = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
  
  if (token) {
    // If we have a token, we return it. 
    // We do NOT attempt to call refreshAuthToken() here because getAuthToken() is synchronous 
    // and is used to initialize Redux state. Returning a Promise causes Redux serialization errors.
    // Token expiration and refresh is handled by the Axios interceptors when API calls return 401.
    return token;
  }
  
  return null;
};

/**
 * Get user information
 * @returns {Object|null} User information or null if not found
 */
export const getUserInfo = () => {
  const userInfoStr = sessionStorage.getItem(USER_INFO_KEY);
  if (userInfoStr) {
    try {
      return JSON.parse(userInfoStr);
    } catch (error) {
      return null;
    }
  }
  return null;
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if user is authenticated
 */
export const isAuthenticated = () => {
  return !!getAuthToken();
};

/**
 * Refresh the authentication token using the refresh token
 * @returns {Promise<string|null>} New token or null if refresh failed
 */
export const refreshAuthToken = async () => {
  const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
  
  if (!refreshToken) {
    clearAuthData();
    return null;
  }
  
  try {
    // Call your API to refresh the token
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
    const response = await fetch(`${apiBaseUrl}/v1/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    
    if (!response.ok) {
      throw new Error('Token refresh failed');
    }
    
    const data = await response.json();
    
    // Extract tokens from the response structure: data.data[0]
    const tokenData = data.data?.[0];
    if (!tokenData) {
      throw new Error('Invalid response structure from refresh API');
    }
    
    // Store the new tokens (backend returns snake_case)
    storeAuthToken(tokenData.access_token, tokenData.refresh_token, tokenData.expires_in);
    
    return tokenData.access_token;
  } catch (error) {
    clearAuthData();
    return null;
  }
};

/**
 * Clear all authentication data
 */
export const clearAuthData = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
  sessionStorage.removeItem(USER_INFO_KEY);
};

/**
 * Add authorization header to fetch options
 * @param {Object} options - Fetch options
 * @returns {Object} Updated fetch options with auth header
 */
export const withAuth = (options = {}) => {
  const token = getAuthToken();
  
  if (!token) {
    return options;
  }
  
  return {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    },
  };
};

/**
 * Parse JWT token to get payload
 * @param {string} token - JWT token
 * @returns {Object|null} Decoded payload or null if invalid
 */
export const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};
